import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Row } from "@amzn/stencil-react-components/layout";
import IconChevronLeftMedium from "@amzn/stencil-react-icons/icons/icon-chevron-left-medium";
import IconChevronRightMedium from "@amzn/stencil-react-icons/icons/icon-chevron-right-medium";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";

/** Gap between chips and arrows, measured from the design. */
const GAP = "9px";

/** A selectable suggested prompt. */
export interface SuggestedPrompt {
  label: string;
  onSelect: () => void;
  dataTestId: string;
}

type Direction = "back" | "forward";

const ARROW_COPY: Record<
  Direction,
  { messageId: string; dataTestId: string; icon: JSX.Element }
> = {
  back: {
    messageId: "chatbot-suggestions-previous",
    dataTestId: "chatbotSuggestionsPrevious",
    icon: <IconChevronLeftMedium aria-hidden />,
  },
  forward: {
    messageId: "chatbot-suggestions-next",
    dataTestId: "chatbotSuggestionsNext",
    icon: <IconChevronRightMedium aria-hidden />,
  },
};

const CarouselArrow = ({
  direction,
  disabled,
  onClick,
}: {
  direction: Direction;
  disabled: boolean;
  onClick: () => void;
}): JSX.Element => {
  const intl = useIntl();
  const { messageId, dataTestId, icon } = ARROW_COPY[direction];

  return (
    <div
      className={`chatbot-suggestions__arrow chatbot-suggestions__arrow--${direction}`}
    >
      <Button
        variant={ButtonVariant.Tertiary}
        onClick={onClick}
        disabled={disabled}
        aria-label={intl.formatMessage({ id: messageId })}
        dataTestId={dataTestId}
        icon={icon}
      />
    </div>
  );
};

/**
 * Horizontally scrolling row of suggested prompts with an arrow at each end.
 * Arrows appear only once the chips actually overflow, and each is disabled at
 * its end of the track.
 *
 * Hand-rolled because Stencil has no carousel, and its `AISuggestions` mandates
 * a gradient action bar this design does not use.
 */
export const SuggestedPrompts = ({
  prompts,
}: {
  prompts: SuggestedPrompt[];
}): JSX.Element => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const syncScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    // 1px tolerance absorbs sub-pixel rounding at fractional zoom levels.
    setCanScrollBack(el.scrollLeft > 1);
    setCanScrollForward(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useLayoutEffect(syncScrollState, [prompts, syncScrollState]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", syncScrollState, { passive: true });
    window.addEventListener("resize", syncScrollState);
    return () => {
      el.removeEventListener("scroll", syncScrollState);
      window.removeEventListener("resize", syncScrollState);
    };
  }, [syncScrollState]);

  const scroll = (direction: Direction) => {
    const el = trackRef.current;
    if (!el) return;
    // Advance by most of a viewport so a partially visible chip stays in view.
    const delta = (direction === "back" ? -1 : 1) * el.clientWidth * 0.8;
    if (typeof el.scrollBy === "function") {
      el.scrollBy({ left: delta, behavior: "smooth" });
    } else {
      // Older engines (and jsdom) have no scrollBy; jump instead of animating.
      el.scrollLeft += delta;
    }
    syncScrollState();
  };

  const hasOverflow = canScrollBack || canScrollForward;

  return (
    <Row alignItems="center" gridGap={GAP} dataTestId="chatbotSuggestions">
      {hasOverflow && (
        <CarouselArrow
          direction="back"
          disabled={!canScrollBack}
          onClick={() => scroll("back")}
        />
      )}
      <Row
        ref={trackRef}
        flex={1}
        gridGap={GAP}
        alignItems="center"
        overflow="auto"
        className="chatbot-suggestions__track"
      >
        {prompts.map(({ label, onSelect, dataTestId }) => (
          <button
            key={label}
            type="button"
            onClick={onSelect}
            className="chatbot-suggestions__chip"
            data-test-id={dataTestId}
          >
            {label}
          </button>
        ))}
      </Row>
      {hasOverflow && (
        <CarouselArrow
          direction="forward"
          disabled={!canScrollForward}
          onClick={() => scroll("forward")}
        />
      )}
    </Row>
  );
};
